export type tWhatsappToken = {
  type: 'text' | 'bold' | 'italic' | 'strike' | 'variable';
  value: string;
};

const MARKER_TYPES: Partial<Record<string, tWhatsappToken['type']>> = {
  '*': 'bold', '_': 'italic', '~': 'strike',
};
const isBoundary = (value: string | undefined): boolean =>
  value === undefined || /[\s\p{P}*_~]/u.test(value);
const isLineBreak = (value: string): boolean => /[\r\n]/u.test(value);

/**
 * Line-local WhatsApp emphasis; backticks remain literal, not a sixth token type.
 * Recursive rendering supplies the enclosing marker to preserve edge boundaries.
 */
export const tokenizeWhatsappText = (
  text: string,
  enclosingMarker?: string,
): tWhatsappToken[] => {
  const characters = Array.from(text);
  const variables = new Map<number, number>();
  // Shield whole placeholders, even when a name contains formatting markers.
  for (let index = 0; index < characters.length; index += 1) {
    if (characters[index] !== '{' || characters[index + 1] !== '{') continue;
    let end = index + 2;
    while (
      end < characters.length &&
      !isLineBreak(characters[end]) &&
      characters[end] !== '}'
    ) {
      end += 1;
    }
    if (end > index + 2 && characters[end] === '}' && characters[end + 1] === '}') {
      variables.set(index, end + 2);
      index = end + 1;
    }
  }

  const tokens: tWhatsappToken[] = [];
  const append = (token: tWhatsappToken): void => {
    const previous = tokens[tokens.length - 1];
    if (token.type === 'text' && previous?.type === 'text') {
      previous.value += token.value;
    } else {
      tokens.push(token);
    }
  };

  let index = 0;
  while (index < characters.length) {
    const variableEnd = variables.get(index);
    if (variableEnd !== undefined) {
      append({ type: 'variable', value: characters.slice(index, variableEnd).join('') });
      index = variableEnd;
      continue;
    }
    const marker = characters[index];
    const type = MARKER_TYPES[marker];
    let close: number | undefined;
    if (
      type && isBoundary(characters[index - 1] ?? enclosingMarker) &&
      characters[index + 1] && !/\s/u.test(characters[index + 1])
    ) {
      // Start at the first content character to skip any placeholder as a unit.
      for (let candidate = index + 1; candidate < characters.length; candidate += 1) {
        if (isLineBreak(characters[candidate])) break;
        const variableClose = variables.get(candidate);
        if (variableClose !== undefined) {
          candidate = variableClose - 1;
          continue;
        }
        if (
          candidate > index + 1 && characters[candidate] === marker &&
          !/\s/u.test(characters[candidate - 1]) &&
          isBoundary(characters[candidate + 1] ?? enclosingMarker)
        ) {
          close = candidate;
          break;
        }
      }
    }
    if (type && close !== undefined) {
      append({ type, value: characters.slice(index + 1, close).join('') });
      index = close + 1;
    } else {
      append({ type: 'text', value: marker });
      index += 1;
    }
  }
  return tokens;
};
